// @flow

// Skipping test coverage for this component since it is not possible to unit test it correctly
/* eslint-disable ta-code-conventions/has-matching-storydoc, ta-code-conventions/has-matching-test */

/**
 * Implementation of impression tracking. See https://docs.tamg.io/pages/viewpage.action?pageId=223941446 for more details.
 */

import * as React from 'react';
import { Mutation } from 'urql';
import isEqual from 'lodash/isEqual';
import { featureIsEnabled } from '@ta/common.features';
import Logging from '@ta/common.logging';

import LogImpressions from './LogImpressions.gql';
import {
  TrackingImpressionContext,
  TrackingImpressionContextProvider,
} from './TrackingImpressionContext';

import type { ImpressionSources, ImpressionAttributes } from './ImpressionAttributes';
import type { TrackingImpressionContextProps } from './TrackingImpressionContext';

type ImpressionProps = {|
  /** The impression source. */
  source: ImpressionSources,
  /** An object of literal attributes to send along with the impression */
  attributes: ImpressionAttributes,
  /** Child nodes to render */
  children: React.Node,
|};

type ImpressionState = {|
  /** The ID of this impression. This is made available to impression children via a new context */
  id?: string,
  /** Whether or not this impression has been logged.
   * This is made available to impression children via a new context.
   */
  isImpressionLogged: boolean,
|};

type InnerProps = {|
  isParentComplete: ?boolean,
  attributes: ImpressionAttributes,
  logImpression: () => void,
|};

/**
 * We want the impression action to run on mount, not render.
 * This component will run the method when it first mounts so that we only
 * get one impression. We make sure that parent impressions complete before
 * logging child impressions
 */
class ImpressionInner extends React.Component<InnerProps> {
  componentDidMount() {
    const { isParentComplete, logImpression } = this.props;
    // Wait for any parent impressions to complete first
    if (isParentComplete) {
      logImpression();
    }
  }

  componentDidUpdate(prevProps: InnerProps) {
    const { attributes, isParentComplete, logImpression } = this.props;
    const { attributes: prevAttributes, isParentComplete: prevIsParentComplete } = prevProps;
    // Log an impression if we have new attributes or the parent impression just finished
    if (!isEqual(attributes, prevAttributes) || (isParentComplete && !prevIsParentComplete)) {
      logImpression();
    }
  }

  render() {
    return null;
  }
}

// eslint-disable-next-line react/no-multi-comp
class Impression extends React.Component<ImpressionProps, ImpressionState> {
  constructor(props: ImpressionProps) {
    super(props);
    this.state = {
      id: undefined,
      isImpressionLogged: false,
    };
  }

  handleMutationCompletion(data: { logImpressions: string[] }) {
    const id =
      data && data.logImpressions && data.logImpressions.length > 0 && data.logImpressions[0];
    const newState = { isImpressionLogged: true, ...(id ? { id } : {}) };
    this.setState(newState);
  }

  render() {
    const { source, attributes, children } = this.props;
    const { id, isImpressionLogged } = this.state;

    if (!featureIsEnabled('react_tracking_impressions')) {
      return children;
    }

    return (
      <TrackingImpressionContext>
        {(contextData: TrackingImpressionContextProps) => {
          const sourceAttributes = attributes[source];
          if (!sourceAttributes) {
            Logging.warn(`Could not find attributes for ${source}`);
          }

          const hasParentImpression = Object.keys(contextData).length > 0;
          const parentId = contextData.impressionId || null;
          const isParentComplete = !hasParentImpression || contextData.isImpressionLogged;

          // this is silly looking but we only want to send the attributes this impression is configured to send.
          // it also helps detect when only the props we care about have changed. Also flow is mad but that's not new.
          // $FlowFixMe
          const impression: ImpressionAttributes = {
            [source]: sourceAttributes,
            parentId: parentId || null,
          };

          return (
            <React.Fragment>
              <Mutation
                /* $FlowFixGraphql (WP-1357) */
                query={LogImpressions}
              >
                {({ executeMutation: logImpressions }) => (
                  <ImpressionInner
                    attributes={impression}
                    isParentComplete={isParentComplete}
                    logImpression={() => {
                      if (sourceAttributes) {
                        this.setState({ isImpressionLogged: false });
                        logImpressions({ impressions: [impression] }).then(
                          ({ data }) => data && this.handleMutationCompletion(data),
                        );
                      }
                    }}
                  />
                )}
              </Mutation>
              <TrackingImpressionContextProvider
                impressionId={id}
                isImpressionLogged={isImpressionLogged}
              >
                {children}
              </TrackingImpressionContextProvider>
            </React.Fragment>
          );
        }}
      </TrackingImpressionContext>
    );
  }
}

export default Impression;
